import { Component, OnInit, ViewChild } from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { UserActions } from '@app/user/store/actions/action-types';
import { IonContent, ModalController, ToastController } from '@ionic/angular';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { PanAppState, PlatformService, jwtDecode } from '@panamax/app-state';
import {
  SortDirections,
  SortableSectionHeader,
} from '@shared/components/sortable-section/models/sortable-section.model';
import { FEATURES } from '@shared/constants/splitio-features';
import { CustomMessage } from '@usf/user-types/custom-message';
import { Observable, Subscription, combineLatest, of } from 'rxjs';
import { catchError, filter, map, take } from 'rxjs/operators';
import { buildNumber } from 'src/buildNumber';
import { HelpModalDesktopComponent } from '../shared/components/help-modal/help-modal-desktop/help-modal-desktop.component';
import { HelpModalTouchComponent } from '../shared/components/help-modal/help-modal-touch/help-modal-touch.component';
import { ModalWithNavComponent } from '../shared/components/modal-with-nav/modal-with-nav.component';
import { PageTitle } from '../shared/constants/page-titles.enum';
import { PATHS } from '../shared/constants/paths';
import { ModalService } from '../shared/services/modal/modal.service';
import { ProfileService } from './services/profile.service';
import { SimpleActionModalComponent } from '../shared/components/simple-action-modal/simple-action-modal.component';
import { ToastService } from '@shared/services/toast/toast.service';
import { MessageTypeEnum } from '@app/ngrx-message/constants/messageTypeEnum';
import { UpcService } from '@shared/services/upc/upc.service';
import { UserKinds } from '@usf/user-types/user';
import { InviteNewUserService } from '@shared/services/invite-new-user/invite-new-user.service';
import { AppService } from '@shared/services/app.service';
import { environment } from 'src/environments/environment';
import { UserService } from '@app/user/services';

@Component({
  selector: 'app-profile',
  templateUrl: './profile.page.html',
  styleUrls: ['./profile.page.scss'],
})
export class ProfilePage implements OnInit {
  @ViewChild(IonContent) content: IonContent;
  buildNumber = buildNumber;
  PATHS = PATHS;

  isDesktop: boolean;
  isMobile: boolean;
  isFormReady: boolean;
  showInviteNewUserSection: boolean;
  isCustomMessagesFlagEnabled = false;
  isInviteNewUserFlagEnabled = false;
  isSuperUser = false;
  isExternalUser = false;

  userKind: string;
  ecUserId: number;

  contactForm: UntypedFormGroup;
  noEmailToast: HTMLIonToastElement;

  customMessages: CustomMessage[];

  profileSub$: Subscription;
  claimTicketSub$: Subscription;
  customMessagesSub$: Subscription;
  onDeleteCustomMessageSub$: Subscription;
  featureFlagCustomMessagesSub$: Subscription;
  getInviteNewUserVisibility$: Subscription;

  isMessagingAdmin$: Observable<any>;
  surveyFeatureFlag$: Observable<boolean>;
  surveyLink$: Observable<string>;
  isUpcDeprecated$: Observable<boolean> = this.upcService.isUpcDeprecated();

  profileAccount: {
    loginEmail?: string;
    loginPhoneNumber?: string;
    userName?: string;
    userSince?: string;
    lastLoginDtm?: string;
  } = {};
  originalInfo: {
    firstName?: string;
    lastName?: string;
    organizationName?: string;
    notificationEmail?: string;
    confirmNotificationEmail?: string;
  } = {};

  appReviewsFlag = FEATURES.split_user_app_reviews.name;
  deleteUserFlag = FEATURES.split_global_alerts_delete_user.name;
  constructor(
    readonly panAppState: PanAppState,
    readonly platformService: PlatformService,
    private router: Router,
    public toastController: ToastController,
    private profileService: ProfileService,
    private translateService: TranslateService,
    private fb: UntypedFormBuilder,
    private modalService: ModalService,
    private modalController: ModalController,
    private actionSubject: ActionsSubject,
    private toastService: ToastService,
    private upcService: UpcService,
    private appService: AppService,
    private inviteNewUserService: InviteNewUserService,
    private userService: UserService,
  ) {}

  ngOnInit() {
    this.contactForm = this.fb.group({
      firstName: [''],
      lastName: [''],
      organizationName: [''],
      notificationEmail: [''],
      confirmNotificationEmail: [''],
    });
    this.isDesktop =
      this.platformService.platformType ===
      this.platformService.platformEnum.desktop;
    this.isMobile =
      this.platformService.platformType ===
      this.platformService.platformEnum.mobile;
  }

  ionViewWillEnter() {
    this.profileService.setProfile();
    this.getUserProfile();
    this.profileService.trackPageLoad();
    this.panAppState.setPageTitle(
      this.translateService.instant(PageTitle.profile),
    );
    this.initializeSurveyLink();
    combineLatest([
      this.panAppState?.feature$([
        FEATURES.split_global_alerts_super_user.name,
      ]),
      this.userService.isSuperUser$(),
    ])
      .pipe(
        map(([flagEnabled, isSuperUser]) => {
          return {
            flagEnabled,
            isSuperUser,
          };
        }),
        take(1),
      )
      .subscribe(res => {
        if (res?.flagEnabled) {
          this.isSuperUser = res?.isSuperUser;
        }
      });
  }

  ionViewDidEnter() {
    if (
      this.contactForm &&
      this.contactForm?.controls['notificationEmail']?.invalid &&
      !this.noEmailToast
    ) {
      this.showNoEmailToast();
    }
    //CUSTOM MESSAGES
    this.featureFlagCustomMessagesSub$ = this.panAppState
      .feature$([FEATURES.split_global_alerts_custom_message.name])
      .subscribe(async on => {
        if (on) {
          //Check AD Role
          this.isCustomMessagesFlagEnabled = true;
          this.checkIfUserIsMessagingAdmin();
          //Get Custom Messages
          this.customMessagesSub$ = this.profileService
            .getCustomMessages()
            .pipe(filter(resp => !!resp))
            .subscribe(resp => {
              this.customMessages = resp;
            });
          //Check if custom message is deleted
          this.onDeleteCustomMessageSub$ = this.actionSubject
            .pipe(ofType(UserActions.deleteCustomMessage))
            .subscribe(data => {
              this.toastService.presentToastMsg(
                this.translateService.instant(
                  'i18n.profilePage.customMessages.customMessageDeletedToast',
                ),
                'green-toast',
                MessageTypeEnum.success,
                [],
              );
              this.profileService.getCustomMessages();
            });
        }
      });
    //INVITE NEW USERS
    this.getInviteNewUserVisibility$ = this.inviteNewUserService
      .getInviteNewUserVisibility()
      ?.subscribe(value => {
        this.showInviteNewUserSection = value;
      });
    //SURVEY
    this.surveyFeatureFlag$ = this.panAppState.feature$([
      FEATURES.split_global_alerts_survey.name,
    ]);

    this.content.scrollToTop();
  }

  matchEmail(
    control: AbstractControl,
    emailOriginal: string,
  ): ValidationErrors | null {
    const email = control.get('notificationEmail');
    const confirm = control.get('confirmNotificationEmail');
    if (!email.dirty && !confirm.dirty) return null;
    if (email.value === emailOriginal && confirm.value === '') return null;
    if (email.value !== confirm.value) return { noMatch: true };
    return null;
  }

  setNewOriginal() {
    this.originalInfo = {
      ...this.contactForm?.value,
      confirmNotificationEmail: '',
    };
    this.contactForm.reset(this.originalInfo);
    if (this.noEmailToast) {
      this.noEmailToast.dismiss();
      this.noEmailToast = null;
    }
  }

  ionViewWillLeave() {
    this.contactForm.reset(this.originalInfo);
    this.profileService.setProfileUnchanged(true);

    if (this.profileSub$) {
      this.profileSub$.unsubscribe();
    }
    if (this.claimTicketSub$) {
      this.claimTicketSub$.unsubscribe();
    }
    if (this.noEmailToast) {
      this.noEmailToast.dismiss();
      this.noEmailToast = null;
    }
    if (this.customMessagesSub$) {
      this.customMessagesSub$.unsubscribe();
    }
    if (this.onDeleteCustomMessageSub$) {
      this.onDeleteCustomMessageSub$.unsubscribe();
    }
    if (this.featureFlagCustomMessagesSub$) {
      this.featureFlagCustomMessagesSub$.unsubscribe();
    }
    if (this.getInviteNewUserVisibility$) {
      this.getInviteNewUserVisibility$.unsubscribe();
    }
  }

  routeTo(path: string) {
    this.router.navigate([path]);
  }

  getUserProfile() {
    this.profileSub$ = this.profileService
      .getProfile()
      .pipe(
        filter(user => !!user),
        catchError(error => {
          return of({ error });
        }),
      )
      .subscribe((user: any) => {
        if (!user.error) {
          this.contactForm.setValue({ ...user.contactInfo });
          this.contactForm.controls['firstName'].setValidators([
            Validators.required,
            Validators.maxLength(25),
          ]);
          this.contactForm.controls['firstName'].updateValueAndValidity();
          this.contactForm.controls['lastName'].setValidators([
            Validators.required,
            Validators.maxLength(25),
          ]);
          this.contactForm.controls['lastName'].updateValueAndValidity();
          this.contactForm.controls['organizationName'].setValidators([
            Validators.maxLength(50),
          ]);
          this.contactForm.controls[
            'organizationName'
          ].updateValueAndValidity();
          this.contactForm.controls['notificationEmail'].setValidators([
            Validators.required,
            Validators.email,
          ]);
          this.contactForm.controls[
            'notificationEmail'
          ].updateValueAndValidity();
          this.contactForm.addValidators([
            control =>
              this.matchEmail(control, this.originalInfo['notificationEmail']),
          ]);
          this.contactForm.updateValueAndValidity();
          this.contactForm.markAsPristine();

          this.originalInfo = { ...user?.contactInfo };
          this.profileAccount = { ...user?.loginAndSecurity };
          this.userKind = user?.userKind;
          this.isExternalUser = user?.userKind === UserKinds.External;
          this.ecUserId = user?.ecomUserId;
          if (!user?.contactInfo?.notificationEmail && !this.noEmailToast) {
            this.showNoEmailToast();
          }
        } else {
          this.openToast({
            toastMessage: 'i18n.profilePage.toast.userError',
            type: 'error',
          });
        }
      });
  }

  async openToast({ toastMessage, type }) {
    let success = type === 'success';
    let message = this.translateService.instant(toastMessage);
    const toast = await this.toastController.create({
      message: message,
      icon: success ? 'checkmark-circle-outline' : 'alert-circle-outline',
      position: 'top',
      cssClass: success ? 'green-toast' : 'red-toast',
      duration: 5000,
    });
    await toast?.present();

    await toast?.onDidDismiss();
  }

  openUPC() {
    this.upcService.openUPC();
  }

  openManageAlerts() {
    this.router.navigate([PATHS.NOTIFICATIONS, PATHS.NOTIFICATION_PREFERENCES]);
  }

  async openHelpModal() {
    if (this.platformService.isTouch.value) {
      this.modalService.setModalOptions(true, ModalWithNavComponent, {
        rootPage: HelpModalTouchComponent,
      });
    } else {
      this.modalService.setModalOptions(
        false,
        ModalWithNavComponent,
        {
          rootPage: HelpModalDesktopComponent,
        },
        'modal-desktop-size-xl',
      );
    }

    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    await modal.present();
  }

  async showNoEmailToast() {
    this.noEmailToast = await this.toastController.create({
      message: this.translateService?.instant('i18n.profilePage.noEmail'),
      icon: 'alert-circle-outline',
      position: 'top',
      cssClass: 'red-toast',
      buttons: [{ icon: 'close-circle' }],
    });
    await this.noEmailToast?.dismiss();
    await this.noEmailToast?.present();
  }

  changeMessageListSort(ev: SortableSectionHeader) {
    if (ev?.headerName === 'startDate') {
      const sortedMessages = [...this.customMessages].filter(
        m => m.startDate.toString().indexOf('1970') < 0,
      );
      const emptyDateMessages = [...this.customMessages].filter(
        m => m.startDate.toString().indexOf('1970') > -1,
      );
      if (ev?.sortDirection === SortDirections.asc) {
        this.customMessages = sortedMessages.sort(
          (a, b) =>
            a['startDate']
              .toString()
              .localeCompare(b['startDate'].toString()) ||
            a['addDtm'].toString().localeCompare(b['addDtm'].toString()),
        );
      } else {
        this.customMessages = sortedMessages.sort(
          (a, b) =>
            b['startDate']
              .toString()
              .localeCompare(a['startDate'].toString()) ||
            b['addDtm'].toString().localeCompare(a['addDtm'].toString()),
        );
      }
      emptyDateMessages.forEach(em => {
        this.customMessages.unshift(em);
      });
    } else if (ev?.headerName === 'text') {
      const sortedMessages = [...this.customMessages];
      if (ev?.sortDirection === SortDirections.asc) {
        this.customMessages = sortedMessages.sort((a, b) =>
          a['text'].localeCompare(b['text']),
        );
      } else {
        this.customMessages = sortedMessages.sort((a, b) =>
          b['text'].localeCompare(a['text']),
        );
      }
    }
  }

  downloadCustomerList(customers: any[]) {
    this.profileService.downloadCustomerList(customers);
  }

  async deleteCustomMessage(messageId: string) {
    this.modalService.setModalOptions(
      this.platformService.isTouch.value,
      SimpleActionModalComponent,
      {
        title: this.translateService.instant(
          'i18n.profilePage.customMessages.deleteMessageModalTitle',
        ),
        content: this.translateService.instant(
          'i18n.profilePage.customMessages.deleteMessageModalContent',
        ),
        leftButtonText: this.translateService.instant(
          'i18n.profilePage.customMessages.deleteMessageModalCancel',
        ),
        rightButtonText: this.translateService.instant(
          'i18n.profilePage.customMessages.deleteMessageModalConfirm',
        ),
        note: null,
        exitButtonForTablet: true,
      },
      'simple-action-modal',
    );

    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    modal.onDidDismiss().then((response: any) => {
      if (response?.data?.dispatchAction) {
        this.profileService.deleteCustomMessage(messageId);
      }
    });
    return await modal.present();
  }

  checkIfUserIsMessagingAdmin(): void {
    this.isMessagingAdmin$ = this.panAppState.accessToken$.pipe(
      filter(token => !!token),
      take(1),
      map(token => {
        const decoded = jwtDecode(token);
        if (decoded) {
          return decoded['usf-claims'].messagingAdmin;
        }
        return '';
      }),
    );
  }

  openDeleteAccountModal() {
    this.appService.getCustomFeedback(
      'delete-moxe-account',
      true,
      environment.usabillaCampaingIdDeleteAccount,
    );
  }

  private initializeSurveyLink() {
    this.surveyLink$ = this.profileService.getSurveyLink();
  }
}
